import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  TableSortLabel,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
  Stack,
  Grid,
  Menu,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import axios from 'axios';
import { API_BASE_URL } from '../constants/config';
import { usePermissions } from '../hooks/usePermissions';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ColumnFilter, { FilterConfig } from './ColumnFilter';
import { getCategoryOptions } from '../constants/categories';

interface InventoryStatusPerShop {
  itemName: string;
  quantityUnit: string;
  totalAdded: number;
  consumable: number;
  blockedduetoinventoryrequests: number;
  category: string;
  lostatfactoy: number;
  soldatfactoy: number;
  stockValueAtManufacturingPriceFactory: number;
  stockValueAtSellingPriceFactory: number;
  sumOfStockValueAtManufacturingPrice: number;
  sumOfStockValueAtSellingPrice: number;
  manufacturingPricePerUnit: number;
  unsoldatshops: { [shopName: string]: number };
  soldatshops: { [shopName: string]: number };
  lostatshop: { [shopName: string]: number };
  stockValueAtManufacturingPriceShops: { [shopName: string]: number };
  stockValueAtSellingPriceShops: { [shopName: string]: number };
}

type Order = 'asc' | 'desc';

interface Item {
  id: number;
  name: string;
  quantityUnit: string;
  pricePerUnit: number | null;
  addAmountToBillInPOS: boolean;
  manufacturedInFactory: boolean;
  category: string;
  consumable: number;
  manufacturingPricePerUnit: number | null;
  avgWeightPerUnit: number | null;
}

interface Shop {
  id: number;
  name: string;
  address: string;
}

const InventoryStatus: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role') || '';
  const { hasPermission } = usePermissions(role);
  
  const [inventoryStatusPerShop, setInventoryStatusPerShop] = useState<InventoryStatusPerShop[]>([]);
  const [isLoadingInventoryStatusPerShop, setIsLoadingInventoryStatusPerShop] = useState(false);
  const [inventoryStatusOrder, setInventoryStatusOrder] = useState<Order>('asc');
  const [inventoryStatusOrderBy, setInventoryStatusOrderBy] = useState<string>('itemName');
  const [columnFilters, setColumnFilters] = useState<Record<string, any>>({});
  
  // Lost and Return Inventory states
  const [items, setItems] = useState<Item[]>([]);
  const [shops, setShops] = useState<Shop[]>([]);
  const [showLostInventoryDialog, setShowLostInventoryDialog] = useState(false);
  const [showReturnInventoryDialog, setShowReturnInventoryDialog] = useState(false);
  const [showAddInventoryDialog, setShowAddInventoryDialog] = useState(false);
  const [lostInventory, setLostInventory] = useState({
    itemName: '',
    quantity: 0,
    notes: '',
    category: '',
    location: 'FACTORY',
    shopName: ''
  });
  const [returnInventory, setReturnInventory] = useState({
    itemName: '',
    quantity: 0,
    notes: '',
    category: '',
    shopName: ''
  });
  const [newInventory, setNewInventory] = useState({
    itemName: '',
    quantity: 0,
    notes: '',
    category: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState<null | HTMLElement>(null);
  
  // Toast notification states
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<'success' | 'error'>('success');

  // Helper functions for toast notifications
  const showSuccess = (message: string) => {
    setToastMessage(message);
    setToastSeverity('success');
    setShowToast(true);
  };

  const showError = (message: string) => {
    setToastMessage(message);
    setToastSeverity('error');
    setShowToast(true);
  };

  useEffect(() => {
    if (token) {
      fetchInventoryStatusPerShop();
    }
  }, [token]);

  const fetchInventoryStatusPerShop = async () => {
    setIsLoadingInventoryStatusPerShop(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/inventory-transactions/status/per-shop`, {
        headers: { Authorization: token }
      });
      setInventoryStatusPerShop(response.data);
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        console.error('Error fetching inventory status:', err);
      }
    } finally {
      setIsLoadingInventoryStatusPerShop(false);
    }
  };


  const handleInventoryStatusSort = (property: string) => {
    const isAsc = inventoryStatusOrderBy === property && inventoryStatusOrder === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setInventoryStatusOrder(newOrder);
    setInventoryStatusOrderBy(property);
  };

  const columnFilterConfigs: FilterConfig[] = [
    { field: 'itemName', type: 'string', label: 'Item Name', placeholder: 'Filter by item name' },
    { field: 'totalAdded', type: 'number', label: 'Total Added', placeholder: 'Filter by total added' },
    { field: 'consumable', type: 'number', label: 'Remaining at Factory', placeholder: 'Filter by remaining' },
    { field: 'blockedduetoinventoryrequests', type: 'number', label: 'Blocked Due to Requests', placeholder: 'Filter by blocked' },
    { field: 'lostatfactoy', type: 'number', label: 'Lost at Factory', placeholder: 'Filter by lost' },
    { field: 'soldatfactoy', type: 'number', label: 'Sold at Factory', placeholder: 'Filter by sold' },
    { field: 'stockValueAtManufacturingPriceFactory', type: 'number', label: 'Stock Value (Manuf. Price) Factory', placeholder: 'Filter by manuf. value' },
    { field: 'stockValueAtSellingPriceFactory', type: 'number', label: 'Stock Value (Selling Price) Factory', placeholder: 'Filter by selling value' },
    { field: 'sumOfStockValueAtManufacturingPrice', type: 'number', label: 'Total Stock Value (Manuf. Price)', placeholder: 'Filter by total manuf. value' },
    { field: 'sumOfStockValueAtSellingPrice', type: 'number', label: 'Total Stock Value (Selling Price)', placeholder: 'Filter by total selling value' },
    { field: 'category', type: 'string', label: 'Category', placeholder: 'Filter by category' }
  ];

  const handleColumnFilterChange = (field: string, value: any) => {
    setColumnFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleColumnFilterClear = (field: string) => {
    setColumnFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[field];
      return newFilters;
    });
  };

  const getFilteredAndSortedInventoryStatus = () => {
    let filtered = inventoryStatusPerShop;
    

    // Apply column filters
    filtered = filtered.filter(item => {
      return Object.entries(columnFilters).every(([field, filterValue]) => {
        if (!filterValue || filterValue === '') return true;
        
        switch (field) {
          case 'itemName':
            return item.itemName.toLowerCase().includes(filterValue.toLowerCase());
          case 'category':
            return item.category.toLowerCase().includes(filterValue.toLowerCase());
          case 'totalAdded':
            return item.totalAdded.toString().includes(filterValue.toString());
          case 'consumable':
            return item.consumable.toString().includes(filterValue.toString());
          case 'blockedduetoinventoryrequests':
            return item.blockedduetoinventoryrequests.toString().includes(filterValue.toString());
          case 'lostatfactoy':
            return item.lostatfactoy.toString().includes(filterValue.toString());
          case 'soldatfactoy':
            return item.soldatfactoy.toString().includes(filterValue.toString());
          case 'stockValueAtManufacturingPriceFactory':
            return item.stockValueAtManufacturingPriceFactory.toString().includes(filterValue.toString());
          case 'stockValueAtSellingPriceFactory':
            return item.stockValueAtSellingPriceFactory.toString().includes(filterValue.toString());
          case 'sumOfStockValueAtManufacturingPrice':
            return item.sumOfStockValueAtManufacturingPrice.toString().includes(filterValue.toString());
          case 'sumOfStockValueAtSellingPrice':
            return item.sumOfStockValueAtSellingPrice.toString().includes(filterValue.toString());
          default:
            return true;
        }
      });
    });
    
    // Apply sorting
    return filtered.sort((a, b) => {
      let aValue: any = a[inventoryStatusOrderBy as keyof InventoryStatusPerShop];
      let bValue: any = b[inventoryStatusOrderBy as keyof InventoryStatusPerShop];
      
      // Handle shop-specific columns
      if (inventoryStatusOrderBy.includes('_unsold_')) {
        const shopName = inventoryStatusOrderBy.replace('_unsold_', '');
        aValue = a.unsoldatshops[shopName] || 0;
        bValue = b.unsoldatshops[shopName] || 0;
      } else if (inventoryStatusOrderBy.includes('_sold_')) {
        const shopName = inventoryStatusOrderBy.replace('_sold_', '');
        aValue = a.soldatshops[shopName] || 0;
        bValue = b.soldatshops[shopName] || 0;
      } else if (inventoryStatusOrderBy.includes('_lost_')) {
        const shopName = inventoryStatusOrderBy.replace('_lost_', '');
        aValue = a.lostatshop[shopName] || 0;
        bValue = b.lostatshop[shopName] || 0;
      } else if (inventoryStatusOrderBy.includes('_manuf_value_')) {
        const shopName = inventoryStatusOrderBy.replace('_manuf_value_', '');
        aValue = a.stockValueAtManufacturingPriceShops[shopName] || 0;
        bValue = b.stockValueAtManufacturingPriceShops[shopName] || 0;
      } else if (inventoryStatusOrderBy.includes('_selling_value_')) {
        const shopName = inventoryStatusOrderBy.replace('_selling_value_', '');
        aValue = a.stockValueAtSellingPriceShops[shopName] || 0;
        bValue = b.stockValueAtSellingPriceShops[shopName] || 0;
      }
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return inventoryStatusOrder === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      const aNum = Number(aValue) || 0;
      const bNum = Number(bValue) || 0;
      
      return inventoryStatusOrder === 'asc'
        ? aNum - bNum
        : bNum - aNum;
    });
  };

  // Add function to calculate totals for filtered inventory status data
  const calculateInventoryStatusTotals = (data: InventoryStatusPerShop[]) => {
    const totals = {
      totalAdded: 0,
      consumable: 0,
      blockedduetoinventoryrequests: 0,
      lostatfactoy: 0,
      soldatfactoy: 0,
      stockValueAtManufacturingPriceFactory: 0,
      stockValueAtSellingPriceFactory: 0,
      sumOfStockValueAtManufacturingPrice: 0,
      sumOfStockValueAtSellingPrice: 0,
      unsoldatshops: {} as { [shopName: string]: number },
      soldatshops: {} as { [shopName: string]: number },
      lostatshop: {} as { [shopName: string]: number },
      stockValueAtManufacturingPriceShops: {} as { [shopName: string]: number },
      stockValueAtSellingPriceShops: {} as { [shopName: string]: number }
    };

    data.forEach(item => {
      totals.totalAdded += item.totalAdded;
      totals.consumable += item.consumable;
      totals.blockedduetoinventoryrequests += item.blockedduetoinventoryrequests;
      totals.lostatfactoy += item.lostatfactoy;
      totals.soldatfactoy += item.soldatfactoy;
      totals.stockValueAtManufacturingPriceFactory += item.stockValueAtManufacturingPriceFactory;
      totals.stockValueAtSellingPriceFactory += item.stockValueAtSellingPriceFactory;
      totals.sumOfStockValueAtManufacturingPrice += item.sumOfStockValueAtManufacturingPrice;
      totals.sumOfStockValueAtSellingPrice += item.sumOfStockValueAtSellingPrice;

      // Aggregate shop-specific data
      Object.keys(item.unsoldatshops).forEach(shopName => {
        totals.unsoldatshops[shopName] = (totals.unsoldatshops[shopName] || 0) + item.unsoldatshops[shopName];
      });
      Object.keys(item.soldatshops).forEach(shopName => {
        totals.soldatshops[shopName] = (totals.soldatshops[shopName] || 0) + item.soldatshops[shopName];
      });
      Object.keys(item.lostatshop).forEach(shopName => {
        totals.lostatshop[shopName] = (totals.lostatshop[shopName] || 0) + item.lostatshop[shopName];
      });
      Object.keys(item.stockValueAtManufacturingPriceShops).forEach(shopName => {
        totals.stockValueAtManufacturingPriceShops[shopName] = (totals.stockValueAtManufacturingPriceShops[shopName] || 0) + item.stockValueAtManufacturingPriceShops[shopName];
      });
      Object.keys(item.stockValueAtSellingPriceShops).forEach(shopName => {
        totals.stockValueAtSellingPriceShops[shopName] = (totals.stockValueAtSellingPriceShops[shopName] || 0) + item.stockValueAtSellingPriceShops[shopName];
      });
    });

    return totals;
  };

  // Add function to export inventory status as PDF
  const exportInventoryStatusToPDF = () => {
    try {
      // Use landscape orientation for better width
      const doc = new jsPDF('landscape');
      const filteredData = getFilteredAndSortedInventoryStatus();
      const currentDate = new Date().toLocaleString();

      // Get shop names from the data
      const shopNames = filteredData.length > 0 
        ? Object.keys(filteredData[0].unsoldatshops || {})
        : [];

      // Add title and metadata
      doc.setFontSize(16);
      doc.text('Inventory Status Report', 14, 15);
      doc.setFontSize(10);
      doc.text(`Generated on: ${currentDate}`, 14, 22);

      // Prepare table data
      const tableData = filteredData.map(item => {
        const row = [
          item.itemName,
          item.totalAdded.toString(),
          item.consumable.toString(),
          item.blockedduetoinventoryrequests.toString(),
          item.lostatfactoy.toString(),
          item.soldatfactoy.toString(),
          item.stockValueAtManufacturingPriceFactory.toFixed(2),
          item.stockValueAtSellingPriceFactory.toFixed(2),
          item.sumOfStockValueAtManufacturingPrice.toFixed(2),
          item.sumOfStockValueAtSellingPrice.toFixed(2),
          item.quantityUnit,
          item.category
        ];

        // Add shop-specific data
        shopNames.forEach(shopName => {
          row.push(
            (item.unsoldatshops[shopName] || 0).toString(),
            (item.soldatshops[shopName] || 0).toString(),
            (item.lostatshop[shopName] || 0).toString(),
            (item.stockValueAtManufacturingPriceShops[shopName] || 0).toFixed(2),
            (item.stockValueAtSellingPriceShops[shopName] || 0).toFixed(2)
          );
        });

        return row;
      });

      // Add total row
      const totals = calculateInventoryStatusTotals(filteredData);
      const totalRow = [
        'TOTAL',
        totals.totalAdded.toString(),
        totals.consumable.toString(),
        totals.blockedduetoinventoryrequests.toString(),
        totals.lostatfactoy.toString(),
        totals.soldatfactoy.toString(),
        totals.stockValueAtManufacturingPriceFactory.toFixed(2),
        totals.stockValueAtSellingPriceFactory.toFixed(2),
        totals.sumOfStockValueAtManufacturingPrice.toFixed(2),
        totals.sumOfStockValueAtSellingPrice.toFixed(2),
        '-',
        '-'
      ];

      // Add shop-specific totals
      shopNames.forEach(shopName => {
        totalRow.push(
          (totals.unsoldatshops[shopName] || 0).toString(),
          (totals.soldatshops[shopName] || 0).toString(),
          (totals.lostatshop[shopName] || 0).toString(),
          (totals.stockValueAtManufacturingPriceShops[shopName] || 0).toFixed(2),
          (totals.stockValueAtSellingPriceShops[shopName] || 0).toFixed(2)
        );
      });

      tableData.push(totalRow);

      // Prepare headers
      const headers = [
        'Item Name',
        'Total Added',
        'Remaining at Factory',
        'Blocked Due to Requests',
        'Lost at Factory',
        'Sold at Factory',
        'Stock Value (Manuf. Price) Factory',
        'Stock Value (Selling Price) Factory',
        'Total Stock Value (Manuf. Price)',
        'Total Stock Value (Selling Price)',
        'Unit',
        'Category'
      ];

      // Add shop-specific headers
      shopNames.forEach(shopName => {
        headers.push(
          `Unsold at ${shopName}`,
          `Sold at ${shopName}`,
          `Lost at ${shopName}`,
          `Stock Value (Manuf.) ${shopName}`,
          `Stock Value (Selling) ${shopName}`
        );
      });

      // Add table to PDF
      autoTable(doc, {
        head: [headers],
        body: tableData,
        startY: 29,
        theme: 'grid',
        styles: { fontSize: 6 },
        headStyles: { fillColor: [41, 128, 185] }
      });

      doc.save('inventory-status-report.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const renderPerShopInventoryStatusTable = () => {
    if (isLoadingInventoryStatusPerShop) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }
    
    // Get shop names from the API response data
    const shopName = localStorage.getItem('shopName');
    const shopNames = shopName 
      ? [shopName] // If shop name is available, only show that shop
      : inventoryStatusPerShop.length > 0 
        ? Object.keys(inventoryStatusPerShop[0].unsoldatshops || {})
        : [];
    
    if (shopNames.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No shops available
          </Typography>
        </Box>
      );
    }
    
    return (
      <TableContainer 
        component={Paper} 
        sx={{ 
          maxHeight: 'calc(100vh - 300px)',
          overflowX: 'auto',
          '& .MuiTableCell-root': {
            padding: '8px 16px',
            fontSize: '0.875rem'
          },
          '& .MuiTable-root': {
            borderCollapse: 'separate',
            borderSpacing: 0
          }
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell 
                sx={{ 
                  minWidth: '150px', 
                  maxWidth: '200px',
                  position: 'sticky',
                  left: 0,
                  zIndex: 3,
                  backgroundColor: 'background.paper',
                  borderRight: '2px solid #e0e0e0'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel
                    active={inventoryStatusOrderBy === 'itemName'}
                    direction={inventoryStatusOrderBy === 'itemName' ? inventoryStatusOrder : 'asc'}
                    onClick={() => handleInventoryStatusSort('itemName')}
                  >
                    Item Name
                  </TableSortLabel>
                  <ColumnFilter
                    config={columnFilterConfigs[0]}
                    value={columnFilters.itemName}
                    onChange={(value) => handleColumnFilterChange('itemName', value)}
                    onClear={() => handleColumnFilterClear('itemName')}
                  />
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ minWidth: '100px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  <TableSortLabel
                    active={inventoryStatusOrderBy === 'totalAdded'}
                    direction={inventoryStatusOrderBy === 'totalAdded' ? inventoryStatusOrder : 'asc'}
                    onClick={() => handleInventoryStatusSort('totalAdded')}
                  >
                    Total Added
                  </TableSortLabel>
                  <ColumnFilter
                    config={columnFilterConfigs[1]}
                    value={columnFilters.totalAdded}
                    onChange={(value) => handleColumnFilterChange('totalAdded', value)}
                    onClear={() => handleColumnFilterClear('totalAdded')}
                  />
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ minWidth: '100px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  <TableSortLabel
                    active={inventoryStatusOrderBy === 'consumable'}
                    direction={inventoryStatusOrderBy === 'consumable' ? inventoryStatusOrder : 'asc'}
                    onClick={() => handleInventoryStatusSort('consumable')}
                  >
                    Remaining at Factory
                  </TableSortLabel>
                  <ColumnFilter
                    config={columnFilterConfigs[2]}
                    value={columnFilters.consumable}
                    onChange={(value) => handleColumnFilterChange('consumable', value)}
                    onClear={() => handleColumnFilterClear('consumable')}
                  />
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ minWidth: '100px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  <TableSortLabel
                    active={inventoryStatusOrderBy === 'blockedduetoinventoryrequests'}
                    direction={inventoryStatusOrderBy === 'blockedduetoinventoryrequests' ? inventoryStatusOrder : 'asc'}
                    onClick={() => handleInventoryStatusSort('blockedduetoinventoryrequests')}
                  >
                    Blocked Due to Requests
                  </TableSortLabel>
                  <ColumnFilter
                    config={columnFilterConfigs[3]}
                    value={columnFilters.blockedduetoinventoryrequests}
                    onChange={(value) => handleColumnFilterChange('blockedduetoinventoryrequests', value)}
                    onClear={() => handleColumnFilterClear('blockedduetoinventoryrequests')}
                  />
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ minWidth: '100px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  <TableSortLabel
                    active={inventoryStatusOrderBy === 'lostatfactoy'}
                    direction={inventoryStatusOrderBy === 'lostatfactoy' ? inventoryStatusOrder : 'asc'}
                    onClick={() => handleInventoryStatusSort('lostatfactoy')}
                  >
                    Lost at Factory
                  </TableSortLabel>
                  <ColumnFilter
                    config={columnFilterConfigs[4]}
                    value={columnFilters.lostatfactoy}
                    onChange={(value) => handleColumnFilterChange('lostatfactoy', value)}
                    onClear={() => handleColumnFilterClear('lostatfactoy')}
                  />
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ minWidth: '100px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  <TableSortLabel
                    active={inventoryStatusOrderBy === 'soldatfactoy'}
                    direction={inventoryStatusOrderBy === 'soldatfactoy' ? inventoryStatusOrder : 'asc'}
                    onClick={() => handleInventoryStatusSort('soldatfactoy')}
                  >
                    Sold at Factory
                  </TableSortLabel>
                  <ColumnFilter
                    config={columnFilterConfigs[5]}
                    value={columnFilters.soldatfactoy}
                    onChange={(value) => handleColumnFilterChange('soldatfactoy', value)}
                    onClear={() => handleColumnFilterClear('soldatfactoy')}
                  />
                </Box>
              </TableCell>
              {/* Stock Value columns - only show for Administrator and Director */}
              {(role === 'Administrator' || role === 'Director') && (
                <>
                  <TableCell align="right" sx={{ minWidth: '120px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <TableSortLabel
                        active={inventoryStatusOrderBy === 'stockValueAtManufacturingPriceFactory'}
                        direction={inventoryStatusOrderBy === 'stockValueAtManufacturingPriceFactory' ? inventoryStatusOrder : 'asc'}
                        onClick={() => handleInventoryStatusSort('stockValueAtManufacturingPriceFactory')}
                      >
                        Stock Value (Manuf. Price) Factory
                      </TableSortLabel>
                      <ColumnFilter
                        config={columnFilterConfigs[6]}
                        value={columnFilters.stockValueAtManufacturingPriceFactory}
                        onChange={(value) => handleColumnFilterChange('stockValueAtManufacturingPriceFactory', value)}
                        onClear={() => handleColumnFilterClear('stockValueAtManufacturingPriceFactory')}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: '120px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <TableSortLabel
                        active={inventoryStatusOrderBy === 'stockValueAtSellingPriceFactory'}
                        direction={inventoryStatusOrderBy === 'stockValueAtSellingPriceFactory' ? inventoryStatusOrder : 'asc'}
                        onClick={() => handleInventoryStatusSort('stockValueAtSellingPriceFactory')}
                      >
                        Stock Value (Selling Price) Factory
                      </TableSortLabel>
                      <ColumnFilter
                        config={columnFilterConfigs[7]}
                        value={columnFilters.stockValueAtSellingPriceFactory}
                        onChange={(value) => handleColumnFilterChange('stockValueAtSellingPriceFactory', value)}
                        onClear={() => handleColumnFilterClear('stockValueAtSellingPriceFactory')}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: '120px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <TableSortLabel
                        active={inventoryStatusOrderBy === 'sumOfStockValueAtManufacturingPrice'}
                        direction={inventoryStatusOrderBy === 'sumOfStockValueAtManufacturingPrice' ? inventoryStatusOrder : 'asc'}
                        onClick={() => handleInventoryStatusSort('sumOfStockValueAtManufacturingPrice')}
                      >
                        Total Stock Value (Manuf. Price)
                      </TableSortLabel>
                      <ColumnFilter
                        config={columnFilterConfigs[8]}
                        value={columnFilters.sumOfStockValueAtManufacturingPrice}
                        onChange={(value) => handleColumnFilterChange('sumOfStockValueAtManufacturingPrice', value)}
                        onClear={() => handleColumnFilterClear('sumOfStockValueAtManufacturingPrice')}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: '120px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <TableSortLabel
                        active={inventoryStatusOrderBy === 'sumOfStockValueAtSellingPrice'}
                        direction={inventoryStatusOrderBy === 'sumOfStockValueAtSellingPrice' ? inventoryStatusOrder : 'asc'}
                        onClick={() => handleInventoryStatusSort('sumOfStockValueAtSellingPrice')}
                      >
                        Total Stock Value (Selling Price)
                      </TableSortLabel>
                      <ColumnFilter
                        config={columnFilterConfigs[9]}
                        value={columnFilters.sumOfStockValueAtSellingPrice}
                        onChange={(value) => handleColumnFilterChange('sumOfStockValueAtSellingPrice', value)}
                        onClear={() => handleColumnFilterClear('sumOfStockValueAtSellingPrice')}
                      />
                    </Box>
                  </TableCell>
                </>
              )}
              <TableCell sx={{ minWidth: '80px' }}>Unit</TableCell>
              <TableCell sx={{ minWidth: '120px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel
                    active={inventoryStatusOrderBy === 'category'}
                    direction={inventoryStatusOrderBy === 'category' ? inventoryStatusOrder : 'asc'}
                    onClick={() => handleInventoryStatusSort('category')}
                  >
                    Category
                  </TableSortLabel>
                  <ColumnFilter
                    config={columnFilterConfigs[10]}
                    value={columnFilters.category}
                    onChange={(value) => handleColumnFilterChange('category', value)}
                    onClear={() => handleColumnFilterClear('category')}
                  />
                </Box>
              </TableCell>
              {/* Dynamic shop columns */}
              {shopNames.map(shopName => (
                <React.Fragment key={shopName}>
                  <TableCell align="right" sx={{ minWidth: '100px', backgroundColor: '#f5f5f5' }}>
                    <TableSortLabel
                      active={inventoryStatusOrderBy === `_unsold_${shopName}`}
                      direction={inventoryStatusOrderBy === `_unsold_${shopName}` ? inventoryStatusOrder : 'asc'}
                      onClick={() => handleInventoryStatusSort(`_unsold_${shopName}`)}
                    >
                      Unsold at {shopName}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: '100px', backgroundColor: '#f5f5f5' }}>
                    <TableSortLabel
                      active={inventoryStatusOrderBy === `_sold_${shopName}`}
                      direction={inventoryStatusOrderBy === `_sold_${shopName}` ? inventoryStatusOrder : 'asc'}
                      onClick={() => handleInventoryStatusSort(`_sold_${shopName}`)}
                    >
                      Sold at {shopName}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: '100px', backgroundColor: '#f5f5f5' }}>
                    <TableSortLabel
                      active={inventoryStatusOrderBy === `_lost_${shopName}`}
                      direction={inventoryStatusOrderBy === `_lost_${shopName}` ? inventoryStatusOrder : 'asc'}
                      onClick={() => handleInventoryStatusSort(`_lost_${shopName}`)}
                    >
                      Lost at {shopName}
                    </TableSortLabel>
                  </TableCell>
                  {/* Stock Value columns for shops - only show for Administrator and Director */}
                  {(role === 'Administrator' || role === 'Director') && (
                    <>
                      <TableCell align="right" sx={{ minWidth: '120px', backgroundColor: '#f5f5f5' }}>
                        <TableSortLabel
                          active={inventoryStatusOrderBy === `_manuf_value_${shopName}`}
                          direction={inventoryStatusOrderBy === `_manuf_value_${shopName}` ? inventoryStatusOrder : 'asc'}
                          onClick={() => handleInventoryStatusSort(`_manuf_value_${shopName}`)}
                        >
                          Stock Value (Manuf.) {shopName}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sx={{ minWidth: '120px', backgroundColor: '#f5f5f5' }}>
                        <TableSortLabel
                          active={inventoryStatusOrderBy === `_selling_value_${shopName}`}
                          direction={inventoryStatusOrderBy === `_selling_value_${shopName}` ? inventoryStatusOrder : 'asc'}
                          onClick={() => handleInventoryStatusSort(`_selling_value_${shopName}`)}
                        >
                          Stock Value (Selling) {shopName}
                        </TableSortLabel>
                      </TableCell>
                    </>
                  )}
                </React.Fragment>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoadingInventoryStatusPerShop ? (
              <TableRow>
                <TableCell colSpan={8 + (role === 'Administrator' || role === 'Director' ? 4 : 0) + shopNames.length * (3 + (role === 'Administrator' || role === 'Director' ? 2 : 0))} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : (
              <>
                {getFilteredAndSortedInventoryStatus().map((item) => (
                    <TableRow key={item.itemName}>
                      <TableCell 
                        sx={{ 
                          position: 'sticky',
                          left: 0,
                          zIndex: 2,
                          backgroundColor: 'background.paper',
                          borderRight: '2px solid #e0e0e0'
                        }}
                      >
                        {item.itemName}
                      </TableCell>
                      <TableCell align="right">{item.totalAdded}</TableCell>
                      <TableCell align="right">{item.consumable}</TableCell>
                      <TableCell align="right">{item.blockedduetoinventoryrequests}</TableCell>
                      <TableCell align="right">{item.lostatfactoy}</TableCell>
                      <TableCell align="right">{item.soldatfactoy}</TableCell>
                      {/* Stock Value columns - only show for Administrator and Director */}
                      {(role === 'Administrator' || role === 'Director') && (
                        <>
                          <TableCell align="right">{item.stockValueAtManufacturingPriceFactory.toFixed(2)}</TableCell>
                          <TableCell align="right">{item.stockValueAtSellingPriceFactory.toFixed(2)}</TableCell>
                          <TableCell align="right">{item.sumOfStockValueAtManufacturingPrice.toFixed(2)}</TableCell>
                          <TableCell align="right">{item.sumOfStockValueAtSellingPrice.toFixed(2)}</TableCell>
                        </>
                      )}
                      <TableCell>{item.quantityUnit}</TableCell>
                      <TableCell>{item.category}</TableCell>
                      {/* Dynamic shop data */}
                      {shopNames.map(shopName => (
                        <React.Fragment key={shopName}>
                          <TableCell align="right" sx={{ backgroundColor: '#f5f5f5' }}>
                            {item.unsoldatshops[shopName] || 0}
                          </TableCell>
                          <TableCell align="right" sx={{ backgroundColor: '#f5f5f5' }}>
                            {item.soldatshops[shopName] || 0}
                          </TableCell>
                          <TableCell align="right" sx={{ backgroundColor: '#f5f5f5' }}>
                            {item.lostatshop[shopName] || 0}
                          </TableCell>
                          {/* Stock Value columns for shops - only show for Administrator and Director */}
                          {(role === 'Administrator' || role === 'Director') && (
                            <>
                              <TableCell align="right" sx={{ backgroundColor: '#f5f5f5' }}>
                                {(item.stockValueAtManufacturingPriceShops[shopName] || 0).toFixed(2)}
                              </TableCell>
                              <TableCell align="right" sx={{ backgroundColor: '#f5f5f5' }}>
                                {(item.stockValueAtSellingPriceShops[shopName] || 0).toFixed(2)}
                              </TableCell>
                            </>
                          )}
                        </React.Fragment>
                      ))}
                    </TableRow>
                  ))}
                {/* Total Row */}
                {(() => {
                  const filteredData = getFilteredAndSortedInventoryStatus();
                  const totals = calculateInventoryStatusTotals(filteredData);
                  return (
                    <TableRow 
                      sx={{ 
                        position: 'sticky',
                        bottom: 0,
                        zIndex: 3,
                        backgroundColor: 'primary.main',
                        '& .MuiTableCell-root': {
                          backgroundColor: 'primary.main',
                          color: 'primary.contrastText',
                          fontWeight: 'bold',
                          borderTop: '2px solid',
                          borderTopColor: 'primary.dark'
                        }
                      }}
                    >
                      <TableCell 
                        sx={{ 
                          position: 'sticky',
                          left: 0,
                          zIndex: 4,
                          backgroundColor: 'primary.main',
                          color: 'primary.contrastText',
                          borderRight: '2px solid',
                          borderRightColor: 'primary.dark',
                          fontWeight: 'bold'
                        }}
                      >
                        TOTAL
                      </TableCell>
                      <TableCell align="right">{totals.totalAdded}</TableCell>
                      <TableCell align="right">{totals.consumable}</TableCell>
                      <TableCell align="right">{totals.blockedduetoinventoryrequests}</TableCell>
                      <TableCell align="right">{totals.lostatfactoy}</TableCell>
                      <TableCell align="right">{totals.soldatfactoy}</TableCell>
                      {/* Stock Value columns - only show for Administrator and Director */}
                      {(role === 'Administrator' || role === 'Director') && (
                        <>
                          <TableCell align="right">{totals.stockValueAtManufacturingPriceFactory.toFixed(2)}</TableCell>
                          <TableCell align="right">{totals.stockValueAtSellingPriceFactory.toFixed(2)}</TableCell>
                          <TableCell align="right">{totals.sumOfStockValueAtManufacturingPrice.toFixed(2)}</TableCell>
                          <TableCell align="right">{totals.sumOfStockValueAtSellingPrice.toFixed(2)}</TableCell>
                        </>
                      )}
                      <TableCell>-</TableCell>
                      <TableCell>-</TableCell>
                      {/* Dynamic shop totals */}
                      {shopNames.map(shopName => (
                        <React.Fragment key={shopName}>
                          <TableCell align="right" sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText', fontWeight: 'bold' }}>
                            {totals.unsoldatshops[shopName] || 0}
                          </TableCell>
                          <TableCell align="right" sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText', fontWeight: 'bold' }}>
                            {totals.soldatshops[shopName] || 0}
                          </TableCell>
                          <TableCell align="right" sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText', fontWeight: 'bold' }}>
                            {totals.lostatshop[shopName] || 0}
                          </TableCell>
                          {/* Stock Value columns for shops - only show for Administrator and Director */}
                          {(role === 'Administrator' || role === 'Director') && (
                            <>
                              <TableCell align="right" sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText', fontWeight: 'bold' }}>
                                {(totals.stockValueAtManufacturingPriceShops[shopName] || 0).toFixed(2)}
                              </TableCell>
                              <TableCell align="right" sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText', fontWeight: 'bold' }}>
                                {(totals.stockValueAtSellingPriceShops[shopName] || 0).toFixed(2)}
                              </TableCell>
                            </>
                          )}
                        </React.Fragment>
                      ))}
                    </TableRow>
                  );
                })()}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const handleLostInventoryClick = async () => {
    try {
      // Fetch items data (always needed)
      const itemsResponse = await axios.get(`${API_BASE_URL}/inventory-items`, {
        headers: { Authorization: token }
      });
      setItems(itemsResponse.data);
      
      // Only fetch shops data for Administrator users
      const shopName = localStorage.getItem('shopName');
      const isShopPOSOperator = role === 'Shop POS Operator and Daily Demand Sender';
      
      if (!isShopPOSOperator) {
        const shopsResponse = await axios.get(`${API_BASE_URL}/shops`, {
          headers: { Authorization: token }
        });
        setShops(shopsResponse.data);
      } else {
        // For Shop POS Operator, add their shop to the shops array so it can be displayed
        setShops([{ id: 0, name: shopName || '', address: '' }]);
      }
      
      // Prepopulate values for Shop POS Operator
      if (isShopPOSOperator) {
        setLostInventory({
          itemName: '',
          quantity: 0,
          notes: '',
          category: '',
          location: 'SHOP',
          shopName: shopName || ''
        });
      } else {
        // Reset for non-shop users
        setLostInventory({
          itemName: '',
          quantity: 0,
          notes: '',
          category: '',
          location: 'FACTORY',
          shopName: ''
        });
      }
      
      setShowLostInventoryDialog(true);
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        console.error('Error fetching data for lost inventory:', err);
      }
    }
  };

  const handleReturnInventoryClick = async () => {
    try {
      // Fetch items data (always needed)
      const itemsResponse = await axios.get(`${API_BASE_URL}/inventory-items`, {
        headers: { Authorization: token }
      });
      setItems(itemsResponse.data);
      
      // Only fetch shops data for Administrator users
      const shopName = localStorage.getItem('shopName');
      const isShopPOSOperator = role === 'Shop POS Operator and Daily Demand Sender';
      
      if (!isShopPOSOperator) {
        const shopsResponse = await axios.get(`${API_BASE_URL}/shops`, {
          headers: { Authorization: token }
        });
        setShops(shopsResponse.data);
      } else {
        // For Shop POS Operator, add their shop to the shops array so it can be displayed
        setShops([{ id: 0, name: shopName || '', address: '' }]);
      }
      
      // Prepopulate values for Shop POS Operator
      if (isShopPOSOperator) {
        setReturnInventory({
          itemName: '',
          quantity: 0,
          notes: '',
          category: '',
          shopName: shopName || ''
        });
      } else {
        // Reset for non-shop users
        setReturnInventory({
          itemName: '',
          quantity: 0,
          notes: '',
          category: '',
          shopName: ''
        });
      }
      
      setShowReturnInventoryDialog(true);
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        console.error('Error fetching data for return inventory:', err);
      }
    }
  };

  const handleLostInventorySubmit = async () => {
    if (!lostInventory.itemName || lostInventory.quantity <= 0) {
      return;
    }

    try {
      setIsSubmitting(true);
      const shopName = localStorage.getItem('shopName');
      const isShopPOSOperator = role === 'Shop POS Operator and Daily Demand Sender';
      
      await axios.post(`${API_BASE_URL}/inventory-transactions/marklost`, {
        itemName: lostInventory.itemName,
        quantity: lostInventory.quantity,
        notes: lostInventory.notes,
        category: lostInventory.category,
        shopName: isShopPOSOperator ? null : (lostInventory.location === 'SHOP' ? lostInventory.shopName : null)
      }, {
        headers: { Authorization: token }
      });
      
      setShowLostInventoryDialog(false);
      setLostInventory({
        itemName: '',
        quantity: 0,
        notes: '',
        category: '',
        location: 'FACTORY',
        shopName: ''
      });
      fetchInventoryStatusPerShop();
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        console.error('Error marking inventory as lost:', err);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReturnInventorySubmit = async () => {
    if (!returnInventory.itemName || returnInventory.quantity <= 0) {
      return;
    }

    try {
      setIsSubmitting(true);
      const shopName = localStorage.getItem('shopName');
      const isShopPOSOperator = role === 'Shop POS Operator and Daily Demand Sender';
      
      await axios.post(`${API_BASE_URL}/inventory-transactions/returnInventory`, {
        itemName: returnInventory.itemName,
        quantity: returnInventory.quantity,
        notes: returnInventory.notes,
        category: returnInventory.category,
        shopName: isShopPOSOperator ? null : returnInventory.shopName
      }, {
        headers: { Authorization: token }
      });
      
      setShowReturnInventoryDialog(false);
      setReturnInventory({
        itemName: '',
        quantity: 0,
        notes: '',
        category: '',
        shopName: ''
      });
      fetchInventoryStatusPerShop();
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        // Show the specific error message from the backend
        const errorMessage = err.response?.data || 'Failed to return inventory';
        showError(errorMessage);
        console.error('Error returning inventory:', err);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddInventoryClick = () => {
    setShowAddInventoryDialog(true);
    fetchItems();
  };

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/inventory-items`, {
        headers: { Authorization: token }
      });
      setItems(response.data);
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        console.error('Error fetching items:', err);
      }
    }
  };

  const handleAddInventory = async () => {
    if (!newInventory.itemName || newInventory.quantity <= 0) {
      return;
    }

    try {
      setIsSubmitting(true);
      await axios.post(`${API_BASE_URL}/inventory-transactions/add`, newInventory, {
        headers: { Authorization: token }
      });
      
      setShowAddInventoryDialog(false);
      setNewInventory({
        itemName: '',
        quantity: 0,
        notes: '',
        category: ''
      });
      fetchInventoryStatusPerShop();
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        console.error('Error adding inventory:', err);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseAddInventoryDialog = () => {
    setShowAddInventoryDialog(false);
    setNewInventory({
      itemName: '',
      quantity: 0,
      notes: '',
      category: ''
    });
  };

  const handleActionsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setActionsMenuAnchor(event.currentTarget);
  };

  const handleActionsMenuClose = () => {
    setActionsMenuAnchor(null);
  };

  const handleActionSelect = (action: string) => {
    handleActionsMenuClose();
    switch (action) {
      case 'export':
        exportInventoryStatusToPDF();
        break;
      case 'add':
        handleAddInventoryClick();
        break;
      case 'lost':
        handleLostInventoryClick();
        break;
      case 'return':
        handleReturnInventoryClick();
        break;
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {isMobile ? (
        <Stack spacing={2} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleActionsMenuOpen}
              sx={{ 
                flex: 1,
                textTransform: 'none',
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Actions
            </Button>
          </Box>
          <Menu
            anchorEl={actionsMenuAnchor}
            open={Boolean(actionsMenuAnchor)}
            onClose={handleActionsMenuClose}
            PaperProps={{
              style: {
                maxHeight: '70vh',
                width: '100%',
                maxWidth: '280px'
              }
            }}
          >
            <MenuItem onClick={() => handleActionSelect('export')}>
              <ListItemText primary="Export to PDF" />
            </MenuItem>
            {hasPermission('add-inventory') && (
              <MenuItem onClick={() => handleActionSelect('add')}>
                <ListItemText primary="Add Inventory" />
              </MenuItem>
            )}
            {hasPermission('mark-inventory-lost') && (
              <MenuItem onClick={() => handleActionSelect('lost')}>
                <ListItemText primary="Lost Inventory" />
              </MenuItem>
            )}
            {hasPermission('return-inventory') && (
              <MenuItem onClick={() => handleActionSelect('return')}>
                <ListItemText primary="Return Inventory" />
              </MenuItem>
            )}
          </Menu>
        </Stack>
      ) : (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 2 }}>
              <Button 
                variant="contained" 
                onClick={exportInventoryStatusToPDF}
                size="small"
              >
                Export to PDF
              </Button>
              {hasPermission('add-inventory') && (
                <Button variant="contained" onClick={handleAddInventoryClick} size="small">
                  Add Inventory
                </Button>
              )}
              {hasPermission('mark-inventory-lost') && (
                <Button variant="contained" color="error" onClick={handleLostInventoryClick} size="small">
                  Lost Inventory
                </Button>
              )}
              {hasPermission('return-inventory') && (
                <Button variant="contained" color="warning" onClick={handleReturnInventoryClick} size="small">
                  Return Inventory
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      )}
      {renderPerShopInventoryStatusTable()}

      {/* Lost Inventory Dialog */}
      <Dialog open={showLostInventoryDialog} onClose={() => setShowLostInventoryDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark Inventory as Lost</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={lostInventory.category}
                onChange={(e) => setLostInventory({ ...lostInventory, category: e.target.value, itemName: '' })}
                label="Category"
                disabled={isSubmitting}
              >
                {getCategoryOptions().map((category) => (
                  <MenuItem key={category.value} value={category.value}>
                    {category.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {lostInventory.category && (
              <FormControl fullWidth>
                <InputLabel>Item</InputLabel>
                <Select
                  value={lostInventory.itemName}
                  onChange={(e) => setLostInventory({ ...lostInventory, itemName: e.target.value })}
                  label="Item"
                  disabled={isSubmitting}
                >
                  {items
                    .filter(item => item.category === lostInventory.category)
                    .map((item) => (
                      <MenuItem key={item.name} value={item.name}>
                        {item.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}

            <TextField
              label="Quantity"
              type="number"
              value={lostInventory.quantity}
              onChange={(e) => setLostInventory({ ...lostInventory, quantity: Number(e.target.value) })}
              fullWidth
              disabled={isSubmitting}
            />

            <FormControl fullWidth>
              <InputLabel>Location</InputLabel>
              <Select
                value={lostInventory.location}
                onChange={(e) => setLostInventory({ ...lostInventory, location: e.target.value })}
                label="Location"
                disabled={isSubmitting || (!!localStorage.getItem('shopName') && role === 'Shop POS Operator and Daily Demand Sender')}
              >
                <MenuItem value="FACTORY">Factory</MenuItem>
                <MenuItem value="SHOP">Shop</MenuItem>
              </Select>
            </FormControl>

            {lostInventory.location === 'SHOP' && (
              <FormControl fullWidth>
                <InputLabel>Shop</InputLabel>
                <Select
                  value={lostInventory.shopName}
                  onChange={(e) => setLostInventory({ ...lostInventory, shopName: e.target.value })}
                  label="Shop"
                  disabled={isSubmitting || (!!localStorage.getItem('shopName') && role === 'Shop POS Operator and Daily Demand Sender')}
                >
                  {shops.map((shop) => (
                    <MenuItem key={shop.name} value={shop.name}>
                      {shop.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              label="Notes"
              multiline
              rows={4}
              value={lostInventory.notes}
              onChange={(e) => setLostInventory({ ...lostInventory, notes: e.target.value })}
              fullWidth
              disabled={isSubmitting}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLostInventoryDialog(false)} disabled={isSubmitting}>Cancel</Button>
          <Button 
            onClick={handleLostInventorySubmit} 
            variant="contained" 
            color="error"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Mark as Lost'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Return Inventory Dialog */}
      <Dialog open={showReturnInventoryDialog} onClose={() => setShowReturnInventoryDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Return Inventory</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={returnInventory.category}
                onChange={(e) => setReturnInventory({ ...returnInventory, category: e.target.value, itemName: '' })}
                label="Category"
                disabled={isSubmitting}
              >
                {getCategoryOptions().map((category) => (
                  <MenuItem key={category.value} value={category.value}>
                    {category.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {returnInventory.category && (
              <FormControl fullWidth required>
                <InputLabel>Item</InputLabel>
                <Select
                  value={returnInventory.itemName}
                  onChange={(e) => setReturnInventory({ ...returnInventory, itemName: e.target.value })}
                  label="Item"
                  disabled={isSubmitting}
                >
                  {items
                    .filter(item => item.category === returnInventory.category)
                    .map((item) => (
                      <MenuItem key={item.name} value={item.name}>
                        {item.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}

            <TextField
              label="Quantity"
              type="number"
              value={returnInventory.quantity}
              onChange={(e) => setReturnInventory({ ...returnInventory, quantity: Number(e.target.value) })}
              fullWidth
              required
              disabled={isSubmitting}
              InputProps={{
                inputProps: { min: 1 }
              }}
            />

            <FormControl fullWidth required>
              <InputLabel>Shop</InputLabel>
              <Select
                value={returnInventory.shopName}
                onChange={(e) => setReturnInventory({ ...returnInventory, shopName: e.target.value })}
                label="Shop"
                disabled={isSubmitting || (!!localStorage.getItem('shopName') && role === 'Shop POS Operator and Daily Demand Sender')}
              >
                {shops.map((shop) => (
                  <MenuItem key={shop.name} value={shop.name}>
                    {shop.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Notes"
              multiline
              rows={4}
              value={returnInventory.notes}
              onChange={(e) => setReturnInventory({ ...returnInventory, notes: e.target.value })}
              fullWidth
              disabled={isSubmitting}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowReturnInventoryDialog(false)} disabled={isSubmitting}>Cancel</Button>
          <Button 
            onClick={handleReturnInventorySubmit} 
            variant="contained" 
            color="primary"
            disabled={isSubmitting || !returnInventory.itemName || !returnInventory.shopName || returnInventory.quantity <= 0}
          >
            {isSubmitting ? 'Submitting...' : 'Return Inventory'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Inventory Dialog */}
      <Dialog open={showAddInventoryDialog} onClose={handleCloseAddInventoryDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Inventory</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>Category</InputLabel>
            <Select
              value={newInventory.category}
              onChange={(e) => setNewInventory({ ...newInventory, category: e.target.value, itemName: '' })}
              label="Category"
              disabled={isSubmitting}
            >
              {getCategoryOptions().map((category) => (
                <MenuItem key={category.value} value={category.value}>
                  {category.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {newInventory.category && (
            <FormControl fullWidth margin="dense">
              <InputLabel>Item</InputLabel>
              <Select
                value={newInventory.itemName}
                onChange={(e) => setNewInventory({ ...newInventory, itemName: e.target.value })}
                label="Item"
                disabled={isSubmitting}
              >
                {items
                  .filter(item => item.category === newInventory.category)
                  .map((item) => (
                    <MenuItem key={item.id} value={item.name}>
                      {item.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          )}
          <TextField
            margin="dense"
            label="Quantity"
            type="number"
            fullWidth
            value={newInventory.quantity}
            onChange={(e) => setNewInventory({ ...newInventory, quantity: parseInt(e.target.value) || 0 })}
            InputProps={{
              inputProps: { min: 1 }
            }}
            disabled={isSubmitting}
          />
          <TextField
            margin="dense"
            label="Notes"
            fullWidth
            multiline
            rows={2}
            value={newInventory.notes}
            onChange={(e) => setNewInventory({ ...newInventory, notes: e.target.value })}
            disabled={isSubmitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddInventoryDialog} disabled={isSubmitting}>Cancel</Button>
          <Button onClick={handleAddInventory} variant="contained" disabled={isSubmitting}>
            {isSubmitting ? <CircularProgress size={24} /> : 'Add Inventory'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notification */}
      <Snackbar
        open={showToast}
        autoHideDuration={6000}
        onClose={() => setShowToast(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setShowToast(false)} severity={toastSeverity}>
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InventoryStatus;
